import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTracking } from '../hooks/useTracking';
import { useTrackingStore } from '../stores/useTrackingStore';
import { useFormatters } from '../hooks/useFormatters';
import { useSessions } from '../hooks/useSessions';
import StatCard from '../components/StatCard';
import ActivityIndicator from '../components/ActivityIndicator';

export default function Record() {
  const navigate = useNavigate();
  const tracking = useTracking();
  const { sessions } = useSessions();
  const lastError = useTrackingStore((s) => s.lastError);
  const currentPointsCount = useTrackingStore((s) => s.currentPoints.length);
  const lastErrorShownRef = useRef(null);
  const { formatSpeed, speedUnit, formatVertical, verticalUnit, formatDistance, distanceUnit, formatDuration, formatDurationShort } = useFormatters();

  // Suggest the most recently used resort as the default for the next
  // recording. Real GPS-based resort detection is a future feature.
  const suggestedResort = useMemo(() => {
    const last = sessions.find((s) => s.resortId);
    return last ? { id: last.resortId, name: last.resortName } : null;
  }, [sessions]);

  // Surface tracking errors (GPS permission denial, no signal, etc.) as
  // toasts so the user knows why their stats are 0. We dedupe by error
  // identity so we don't spam the user on every render.
  useEffect(() => {
    if (!lastError) return;
    const key = lastError.message || String(lastError);
    if (lastErrorShownRef.current === key) return;
    lastErrorShownRef.current = key;
    const msg = /permission|denied/i.test(key)
      ? 'Location permission denied. Enable it in Settings to record sessions.'
      : /unavailable|signal/i.test(key)
        ? 'GPS signal unavailable. Move outdoors or wait a moment.'
        : `Tracking error: ${key}`;
    toast.error(msg);
  }, [lastError]);

  // Count run/lift numbers from segments
  const runCount = tracking.stats.totalRuns;
  const liftCount = tracking.segments.filter((s) => s.type === 'lift').length;

  // Determine current activity display
  const activityType = tracking.currentActivity?.type || 'stopped';

  const handleStart = () => {
    tracking.start(suggestedResort?.id, suggestedResort?.name);
  };

  const handlePause = () => {
    if (tracking.isPaused) {
      tracking.resume();
    } else {
      tracking.pause();
    }
  };

  const handleStop = async () => {
    const session = await tracking.stop();
    if (session) {
      navigate(`/summary/${session.id}`);
    }
  };

  // Last session for idle screen
  const lastSession = sessions.length > 0 ? sessions[0] : null;

  // ─────────────────────────────────────────────────────────────────────────
  // IDLE STATE (not recording)
  // ─────────────────────────────────────────────────────────────────────────
  if (!tracking.isRecording) {
    return (
      <div className="min-h-screen bg-black text-white pb-24">
        {/* Header */}
        <div className="px-4 pt-[env(safe-area-inset-top)] pt-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold tracking-tight">
              Powder<span className="text-blue-400">Log</span>
            </h1>
            <Link to="/premium" className="text-xs text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full font-medium">
              PRO
            </Link>
          </div>
        </div>

        {/* Resort suggestion */}
        <div className="px-4 mb-6">
          {suggestedResort ? (
            <div className="bg-zinc-900 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-xs uppercase tracking-wider">Resort</p>
                  <h2 className="text-lg font-bold">{suggestedResort.name}</h2>
                  <p className="text-zinc-500 text-xs mt-0.5">Suggested from your last session</p>
                </div>
                <Link
                  to="/resorts"
                  className="text-blue-400 text-xs font-medium min-h-[44px] flex items-center px-2"
                >
                  Change
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900 rounded-2xl p-4 text-center">
              <p className="text-zinc-500 text-sm">No resort selected</p>
              <Link to="/resorts" className="text-blue-400 text-sm font-medium mt-1 inline-block min-h-[44px]">
                Browse Resorts
              </Link>
            </div>
          )}
        </div>

        {/* Start Recording Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleStart}
            className="w-40 h-40 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 active:scale-95 transition-all flex flex-col items-center justify-center shadow-lg shadow-blue-500/30"
          >
            <svg className="w-12 h-12 text-white mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="4" fill="currentColor" />
            </svg>
            <span className="text-white font-bold text-base">Start Recording</span>
          </button>
        </div>

        {/* Last Session Quick Stats */}
        {lastSession && (
          <div className="px-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-zinc-400 text-xs uppercase tracking-wider font-medium">Last Session</h3>
              <Link to="/logbook" className="text-blue-400 text-xs font-medium">View All</Link>
            </div>
            <Link to={`/session/${lastSession.id}`} className="block bg-zinc-900 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white font-semibold">{lastSession.resortName || 'Unknown Resort'}</p>
                  <p className="text-zinc-500 text-xs">
                    {lastSession.startTime ? new Date(lastSession.startTime).toLocaleDateString() : 'Recent'}
                  </p>
                </div>
                <svg className="w-5 h-5 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-2xl font-bold tabular-nums">{lastSession.stats?.totalRuns || 0}</p>
                  <p className="text-zinc-500 text-xs">Runs</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold tabular-nums">{formatVertical(lastSession.stats?.totalVertical || 0)}</p>
                  <p className="text-zinc-500 text-xs">Vert ({verticalUnit})</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold tabular-nums">{formatSpeed(lastSession.stats?.maxSpeed || 0)}</p>
                  <p className="text-zinc-500 text-xs">Top ({speedUnit})</p>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Quick Links */}
        <div className="px-4">
          <div className="grid grid-cols-2 gap-3">
            <Link to="/logbook" className="bg-zinc-900 rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition-transform">
              <svg className="w-8 h-8 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              <div>
                <p className="text-white font-semibold text-sm">Logbook</p>
                <p className="text-zinc-500 text-xs">{sessions.length} sessions</p>
              </div>
            </Link>
            <Link to="/stats" className="bg-zinc-900 rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition-transform">
              <svg className="w-8 h-8 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 20V10" />
                <path d="M12 20V4" />
                <path d="M6 20v-6" />
              </svg>
              <div>
                <p className="text-white font-semibold text-sm">Season Stats</p>
                <p className="text-zinc-500 text-xs">2025-2026</p>
              </div>
            </Link>
          </div>
        </div>

      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RECORDING STATE
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white pb-40">
      {/* Timer Header */}
      <div className="px-4 pt-[env(safe-area-inset-top)] pt-4">
        <div className="flex items-center justify-between mb-1">
          {tracking.resortName && (
            <p className="text-zinc-400 text-sm font-medium truncate flex-1">{tracking.resortName}</p>
          )}
          {tracking.isPaused && (
            <span className="text-yellow-400 text-xs font-bold bg-yellow-500/10 px-2 py-0.5 rounded-full animate-pulse">
              PAUSED
            </span>
          )}
          {!tracking.isPaused && (
            <span className="text-red-400 text-xs font-bold bg-red-500/10 px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
              REC
            </span>
          )}
        </div>

        {/* Elapsed Time */}
        <div className="text-center mb-4">
          <p className="text-5xl font-bold tabular-nums tracking-tight">
            {formatDuration(tracking.elapsed)}
          </p>
        </div>

        {/* Acquiring-GPS state: no points yet during an active recording. */}
        {currentPointsCount === 0 && !tracking.isPaused && (
          <div className="flex justify-center mb-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-full px-4 py-1.5 text-xs text-zinc-400 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              Acquiring GPS…
            </div>
          </div>
        )}
      </div>

      {/* Activity Indicator */}
      <div className="flex justify-center mb-4">
        <ActivityIndicator
          type={activityType}
          runNumber={activityType === 'skiing' || activityType === 'traversing' ? runCount + 1 : undefined}
          liftNumber={activityType === 'lift' ? liftCount + 1 : undefined}
        />
      </div>

      {/* Stats Grid - 2x3. The live "Runs" counter was removed because the
          live and post-processed run-segmentation algorithms can disagree
          (the live one under-counts on short stops); DaySummary shows the
          authoritative final count. */}
      <div className="px-4 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            value={formatVertical(tracking.stats.totalVertical)}
            label="Vertical"
            unit={verticalUnit}
            size="md"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20V4M12 4l-4 4M12 4l4 4" />
              </svg>
            }
          />
          <StatCard
            value={formatSpeed(tracking.stats.maxSpeed)}
            label="Top Speed"
            unit={speedUnit}
            size="md"
            color="text-orange-400"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            }
          />
          <StatCard
            value={formatDistance(tracking.stats.totalDistance)}
            label="Distance"
            unit={distanceUnit(tracking.stats.totalDistance)}
            size="md"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            }
          />
          <StatCard
            value={formatDurationShort(tracking.stats.activeTime)}
            label="Time Skiing"
            size="sm"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            }
          />
          <StatCard
            value={formatDurationShort(tracking.stats.liftTime)}
            label="Time on Lifts"
            size="sm"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 20V4M20 20V4M4 8h16" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Current Speed & Altitude */}
      <div className="px-4 mb-4">
        <div className="bg-zinc-900 rounded-2xl p-4">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <p className="text-3xl font-bold tabular-nums text-blue-400">
                {formatSpeed(tracking.currentSpeed)}
              </p>
              <p className="text-zinc-500 text-xs">{speedUnit}</p>
            </div>
            <div className="w-px h-12 bg-zinc-800" />
            <div className="text-center">
              <p className="text-3xl font-bold tabular-nums text-green-400">
                {Math.round(tracking.currentAltitude)}
              </p>
              <p className="text-zinc-500 text-xs">altitude (m)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent px-4 pb-[env(safe-area-inset-bottom)] pb-6 pt-8">
        <div className="flex items-center justify-center gap-6 max-w-md mx-auto">
          {/* Pause/Resume Button */}
          <button
            onClick={handlePause}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all active:scale-90 ${
              tracking.isPaused
                ? 'bg-green-500 shadow-lg shadow-green-500/30'
                : 'bg-yellow-500 shadow-lg shadow-yellow-500/30'
            }`}
          >
            {tracking.isPaused ? (
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            )}
          </button>

          {/* Stop Button */}
          <button
            onClick={handleStop}
            className="w-20 h-20 rounded-full bg-red-500 shadow-lg shadow-red-500/30 flex items-center justify-center transition-all active:scale-90"
          >
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
